import { useCallback, useEffect, useRef, useState } from 'react';

interface AutoSaveOptions {
  url: string;
  name: string;
  debounceMs?: number;
}

function getCsrfToken(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
  return meta?.content || '';
}

function buildFormData(name: string, value: string | string[] | File): FormData {
  const formData = new FormData();
  if (Array.isArray(value)) {
    value.forEach(v => formData.append(name, v));
  } else {
    formData.append(name, value);
  }
  return formData;
}

export function useAutoSave({ url, name, debounceMs = 500 }: AutoSaveOptions) {
  const [autoSaveError, setAutoSaveError] = useState<string | undefined>();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController>();

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const fieldKey = name.replace(/.*\[(\w+)\]$/, '$1');

  const save = useCallback((value: string | string[] | File) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetch(url, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': getCsrfToken(),
            'Accept': 'application/json',
          },
          body: buildFormData(name, value),
          signal: controller.signal,
        });

        if (!response.ok) {
          const data = await response.json();
          setAutoSaveError(data.errors?.[fieldKey]);
        } else {
          setAutoSaveError(undefined);
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
      }
    }, debounceMs);
  }, [url, name, fieldKey, debounceMs]);

  return { autoSaveError, save };
}
